import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import selectorUser from '@redux/user/selectors';

import { useGetProductEnums, useGetProductFullView } from 'hooks';

import { isEmpty, every } from 'lodash';

import { checkIsSuperAdmin, checkOwnerOneItem } from 'utils';

import {
  MEMBER_TYPE_MANUFACTURER,
  MEMBER_TYPE_SHARED_ONLY,
} from 'static/Constants';
import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';
import { useCheckPermissions } from 'hooks';

import useCheckSnapshotForRetailer from './useCheckSnapshotForRetailer';

const useGetDproperties = (productItemModules) => {
  const dProperties = useMemo(() => {
    if (!productItemModules) return null;

    return productItemModules?.productItemModules?.find(
      (module) => module?.moduleName === 'DProperties'
    );
  }, [JSON.stringify(productItemModules)]);

  return dProperties;
};

const useGetGridData = (dProperties, columnDef) => {
  const data = useMemo(() => {
    let nextData = {};

    if (!dProperties) return [];

    for (const [fieldName, value] of Object.entries(dProperties?.data)) {
      if (columnDef.map((fieldItem) => fieldItem?.field).includes(fieldName)) {
        nextData[fieldName] = value;
      }
    }

    return every(nextData, isEmpty) ? [] : [nextData];
  }, [JSON.stringify(dProperties), columnDef]);

  return data;
};

const useGetAllergenGridData = (dProperties, productSchema) => {
  const data = useMemo(() => {
    let nextData = [];

    if (!dProperties || !productSchema) return nextData;

    const allergenFieldList = productSchema
      ?.find((schemaItem) => schemaItem?.moduleName === 'DProperties')
      ?.moduleProperties.filter(
        (propertyItem) => propertyItem?.propertyGroup === 'Allergens'
      );

    for (const [fieldName, value] of Object.entries(dProperties?.data)) {
      const foundField = allergenFieldList?.find(
        (allergenFieldItem) => allergenFieldItem?.propertyName === fieldName
      );

      if (foundField) {
        let firstValue, secondValue;

        const inverseValue = (currentValue) => {
          if (!currentValue) return '';
          if (currentValue.includes('Yes')) return 'No';
          if (currentValue.includes('Not Applicable')) return 'Not Applicable';
          if (currentValue.includes('No')) return 'Yes';
        };

        if (value && foundField?.propertyName !== 'AllergenStatement') {
          const currentValue = value.split(':')?.[1];

          if (value.includes('Intentionally Present as Ingredient:')) {
            firstValue = currentValue;
            secondValue = inverseValue(currentValue);
          } else {
            firstValue = inverseValue(currentValue);
            secondValue = currentValue;
          }
        }

        nextData = [
          ...nextData,
          {
            propertyName: foundField?.propertyName,
            Allergens:
              foundField?.propertyDescription ||
              foundField?.propertyDisplayName,
            IntentionallyPresentAsIngredient: firstValue,
            PresentAsResultPotentialContamination: secondValue,
            OriginalValue: value,
          },
        ];
      }
    }

    return nextData;
  }, [JSON.stringify(dProperties), productSchema]);

  return data;
};

const enumCheckboxToSelect = [
  { value: true, displayName: 'Yes' },
  { value: false, displayName: 'No' },
];

const useGetEnumOptions = ({ optionMapping } = { optionMapping: null }) => {
  const { productEnums } = useGetProductEnums();

  const options = useMemo(() => {
    const enumList = [
      'BasicUomEnum',
      'DaymonNonBinaryLogicCodeEnum',
      'OriginCodeEnum',
      'ReligiousClaimCodeEnum',
      'LevelOfContainmentCodeEnum',
      'ContainmentSourceEnum',
      'PhysicalAndChemicalCharacteristicsParameterCodeEnum',
      'MicrobiologicalCharacteristicsParameterCodeEnum',
      'YesNoEnum',
      'NonBinaryLogicEnumerationEnum',
      'UsdaGradingEnum',
      'FreshSeafoodSourceEnum',
      'OnOffEnum',
      'KosherCertificationEnum',
      'HalalCertifierEnum',
      'NonGMOCertifierEnum',
      'PackagingRecyclingSchemeCodeEnum',
      'PackageTypeCode_GDSN_Enum',
      'PackagingMaterialTypeCodeEnum',
      'LanguageCodeEnum',
      'ClaimsEnum',
    ];
    let qaSpecEnums = {};

    productEnums.forEach((enumItem) => {
      const foundEnumName = enumList.find(
        (enumListItem) => enumListItem === enumItem?.enumName
      );
      if (foundEnumName) {
        qaSpecEnums[foundEnumName] = optionMapping
          ? enumItem?.enumProperties.map(optionMapping)
          : enumItem?.enumProperties;
      }

      if (qaSpecEnums?.length === enumList) {
        return;
      }
    });

    return {
      ingredient: {
        organicOrBio: qaSpecEnums?.DaymonNonBinaryLogicCodeEnum || [],
        origin: qaSpecEnums?.OriginCodeEnum || [],
        religiousClaim: qaSpecEnums?.ReligiousClaimCodeEnum || [],
        gmo: qaSpecEnums?.DaymonNonBinaryLogicCodeEnum || [],
        ionized: qaSpecEnums?.DaymonNonBinaryLogicCodeEnum || [],
        allergens: qaSpecEnums?.DaymonNonBinaryLogicCodeEnum || [],
        bioengineered: qaSpecEnums?.DaymonNonBinaryLogicCodeEnum || [],
      },
      allergen: {
        levelOfContainment: qaSpecEnums?.LevelOfContainmentCodeEnum || [],
        containmentSource: qaSpecEnums?.ContainmentSourceEnum || [],
      },
      productSpec: {
        polyflour: {
          productContainPfas: qaSpecEnums?.['YesNoEnum'],
          productPackagingContainPfas: qaSpecEnums?.['YesNoEnum'],
          conductedAnyPFSTesting: qaSpecEnums?.['YesNoEnum'],
          conductedAnyPFASTestingProductPackaging: qaSpecEnums?.['YesNoEnum'],
        },
        dietaryCertificate: {},
        physicAndChem: {
          parameter:
            qaSpecEnums?.PhysicalAndChemicalCharacteristicsParameterCodeEnum ||
            [],
        },
        microbiological: {
          parameter:
            qaSpecEnums?.MicrobiologicalCharacteristicsParameterCodeEnum || [],
        },
      },
      freshMeat: {
        suitableForVetgetarianLogo: qaSpecEnums?.['YesNoEnum'],
        irradiated: qaSpecEnums?.['NonBinaryLogicEnumerationEnum'],
        fortification: qaSpecEnums?.['YesNoEnum'],
        freeRange: qaSpecEnums?.['YesNoEnum'],
        usdaGrading: qaSpecEnums?.['UsdaGradingEnum'],
        dryAged: qaSpecEnums?.['YesNoEnum'],
        coldSmoked: qaSpecEnums?.['YesNoEnum'],
      },
      freshSeafood: {
        source: qaSpecEnums?.['FreshSeafoodSourceEnum'],
        feeledDeveined: qaSpecEnums?.['YesNoEnum'],
        tailOffOrOn: qaSpecEnums?.['OnOffEnum'],
      },
      packaging: {
        productContainsBioengineeredIngredients: qaSpecEnums?.['YesNoEnum'],
        frontOfPackIcons: qaSpecEnums?.['YesNoEnum'],
        recyclabel: qaSpecEnums?.['YesNoEnum'],
        recyclableType: qaSpecEnums?.['PackagingRecyclingSchemeCodeEnum'],
        packagingType: qaSpecEnums?.['PackageTypeCode_GDSN_Enum'],
        packagingMaterial: qaSpecEnums?.['PackagingMaterialTypeCodeEnum'],
        qrCode: enumCheckboxToSelect,
        bilingual: qaSpecEnums?.['YesNoEnum'],
        uom: qaSpecEnums?.['BasicUomEnum'],
        secondaryLanguage: qaSpecEnums?.['LanguageCodeEnum'],
        dietaryCertificate: {
          kosherCertifier: qaSpecEnums?.['KosherCertificationEnum'],
          halalCertifier: qaSpecEnums?.['HalalCertifierEnum'],
          nonGMOCertifier: qaSpecEnums?.['NonGMOCertifierEnum'],
        },
        hasFoodGradePlasticsCertifier: enumCheckboxToSelect,
        recycledMaterial: enumCheckboxToSelect,
        soyInk: enumCheckboxToSelect,
        vegetableBasedInk: enumCheckboxToSelect,
      },
      claims: {
        contains: qaSpecEnums?.['ClaimsEnum'],
        doesNotContain: qaSpecEnums?.['ClaimsEnum'],
      },
    };
  }, [productEnums, optionMapping]);

  return options;
};

export const PERMISSION_EDIT_QA_SPEC = [
  {
    action: ABILITY_ACTION.EDIT,
    subject: ABILITY_SUBJECT.QA_SPECIFICATION,
  },
];

export const PERMISSION_EDIT_QA_SPEC_CARS = [
  {
    action: ABILITY_ACTION.EDIT,
    subject: ABILITY_SUBJECT.QA_SPECIFICATION_CARS,
  },
];

export const PERMISSION_VIEW_QA_SPEC_CARS = [
  {
    action: ABILITY_ACTION.VIEW,
    subject: ABILITY_SUBJECT.QA_SPECIFICATION_CARS,
  },
];

export const PERMISSION_EDIT_NBE = [
  {
    action: ABILITY_ACTION.EDIT,
    subject: ABILITY_SUBJECT.NBE,
  },
];

export const PERMISSION_VIEW_NBE = [
  {
    action: ABILITY_ACTION.VIEW,
    subject: ABILITY_SUBJECT.NBE,
  },
];
export const PERMISSION_VIEW_POWER_BI = [
  {
    action: ABILITY_ACTION.VIEW,
    subject: ABILITY_SUBJECT.POWER_BI,
  },
];
export const PERMISSION_VIEW_POWER_BI_BETA = [
  {
    action: ABILITY_ACTION.VIEW,
    subject: ABILITY_SUBJECT.POWER_BI_BETA,
  },
];

export const PERMISSION_REVIEW_PRODUCT_BULK_UPDATE = [
  {
    action: ABILITY_ACTION.VIEW,
    subject: ABILITY_SUBJECT.REVIEW_PRODUCT_BULK_UPDATE,
  },
];

export const PERMISSION_VIEW_QA_COMMENT_WORKFLOW = [
  {
    action: ABILITY_ACTION.VIEW,
    subject: ABILITY_SUBJECT.QA_COMMENT_WORKFLOW,
  },
];
export const PERMISSION_EDIT_COMMENT = [
  {
    action: ABILITY_ACTION.EDIT,
    subject: ABILITY_SUBJECT.EDIT_COMMENT,
  },
];

export const checkEditForSupplier = ({ productFull, companyInfo }) => {
  const memberType = companyInfo?.member?.memberType;

  const isSupplier =
    MEMBER_TYPE_SHARED_ONLY === memberType ||
    MEMBER_TYPE_MANUFACTURER === memberType;

  const supplierExisted = productFull?.suppliers?.some(
    (supplier) => supplier?.id === companyInfo?.member?.id
  );

  if (isSupplier && supplierExisted) return true;

  if (isSupplier && productFull?.editable) {
    return true;
  }

  return false;
};

const useCheckAllowEdit = (productIdProps) => {
  const params = useParams();

  const userInfo = useSelector(selectorUser.makeSelectUserInfo()) || {};
  const { productFull } = useGetProductFullView({
    productId: productIdProps || params?.id,
  });

  const hasEditQaSpecPermission = useCheckPermissions(PERMISSION_EDIT_QA_SPEC);

  const { roles, isSuperMember } = userInfo ?? {};
  const isSuperAdmin = checkIsSuperAdmin(roles);
  const isOwner = checkOwnerOneItem(productFull);

  const hasEditPermissionForSupplier = checkEditForSupplier({
    productFull,
    companyInfo: userInfo,
  });

  const checkAllowQAEdit = () => {
    if (isSuperAdmin) return true;

    if (!hasEditQaSpecPermission) return false;

    if (isSuperMember) return true;

    if (isOwner) return true;

    if (hasEditPermissionForSupplier) return true;

    return false;
  };

  return checkAllowQAEdit();
};

const useCheckAllowViewNBE = () => {
  const userInfo = useSelector(selectorUser.makeSelectUserInfo()) || {};
  const hasViewNBEPermission = useCheckPermissions(PERMISSION_VIEW_NBE);
  const { roles } = userInfo ?? {};
  const isSuperAdmin = checkIsSuperAdmin(roles);
  const checkAllowViewNBE = () => {
    return isSuperAdmin || hasViewNBEPermission;
  };

  return checkAllowViewNBE();
};

const useCheckAllowEditNBE = () => {
  const userInfo = useSelector(selectorUser.makeSelectUserInfo()) || {};
  const hasEditNBEPermission = useCheckPermissions(PERMISSION_EDIT_NBE);
  const { roles } = userInfo ?? {};
  const isSuperAdmin = checkIsSuperAdmin(roles);

  const checkAllowEditNBE = () => {
    return isSuperAdmin || hasEditNBEPermission;
  };
  return checkAllowEditNBE();
};

const useCheckAllowViewQaCommentWorkflow = () => {
  const hasViewQaCommentWorkflowPermission = useCheckPermissions(
    PERMISSION_VIEW_QA_COMMENT_WORKFLOW
  );

  const checkAllowViewComments = () => {
    return hasViewQaCommentWorkflowPermission;
  };
  return checkAllowViewComments();
};

const useCheckAllowReviewProductBulkUpdate = () => {
  const hasReviewBulkProductUpdatePermission = useCheckPermissions(
    PERMISSION_REVIEW_PRODUCT_BULK_UPDATE
  );
  const checkAllowReviewBulkProductUpdate = () => {
    return hasReviewBulkProductUpdatePermission;
  };
  return checkAllowReviewBulkProductUpdate();
};

const useCheckAllowEditComments = () => {
  const { id: productId } = useParams();
  const { productFull } = useGetProductFullView({ productId });
  const status = productFull?.status;
  const hasEditQaCommentPermission = useCheckPermissions(
    PERMISSION_EDIT_COMMENT
  );

  const checkAllowEditComments = () => {
    const nonEditableStatuses = ['Deleted', 'Discontinued', 'Archived'];
    const isEditableStatus = !nonEditableStatuses.includes(status);
    return hasEditQaCommentPermission && isEditableStatus;
  };
  return checkAllowEditComments();
};

const useSelectProductFullDetailData = ({ productId }) => {
  const { productFull } = useGetProductFullView({ productId });

  return productFull || {};
};

export {
  useCheckAllowEdit,
  useCheckAllowViewNBE,
  useCheckAllowEditNBE,
  useCheckAllowReviewProductBulkUpdate,
  useCheckAllowViewQaCommentWorkflow,
  useCheckAllowEditComments,
  useGetDproperties,
  useGetGridData,
  useGetAllergenGridData,
  useGetEnumOptions,
  useSelectProductFullDetailData,
  useCheckSnapshotForRetailer,
};
